import React, { useState, useEffect, useMemo } from "react";
import {
  Container,
  Grid,
  Typography,
  Card,
  CardContent,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
} from "@mui/material";
import {
  Add as AddIcon,
  AssignmentLate as AssignmentLateIcon,
  AssignmentTurnedIn as AssignmentTurnedInIcon,
  AssignmentInd as AssignmentIndIcon,
} from "@mui/icons-material";
import Layout from "../components/layout/LegalLayout";
import axios from "axios";
import config from "../config.js";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";

const CASE_TYPES = [
  "Tax Audit",
  "Compliance Review",
  "Legal Consultation",
  "Contract Review",
  "Regulatory Filing",
  "Dispute Resolution",
];

const CASE_STATUSES = [
  "In Progress",
  "Pending Review",
  "Waiting for Client",
  "Completed",
  "On Hold",
];

const PRIORITIES = ["Low", "Medium", "High"];

const CaseTracking = () => {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const { API_BASE_URL } = config;
  const selectedCompanyId = useSelector((state) => state.kyc.selectedCompanyId);
  const [filters, setFilters] = useState({
    status: "",
    priority: "",
    assignedTo: "",
    type: "",
  });

  const fetchCases = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(
        `${API_BASE_URL}/api/v1/legalcases/${selectedCompanyId}`
      );
      setCases(response.data.data || []);
    } catch (error) {
      setError(
        "Error fetching cases: " +
          (error.response?.data?.message || error.message)
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedCompanyId) {
      fetchCases();
    }
  }, [selectedCompanyId]);

  const filteredCases = useMemo(() => {
    return cases
      .filter(
        (caseItem) =>
          (filters.status ? caseItem.status === filters.status : true) &&
          (filters.priority ? caseItem.priority === filters.priority : true) &&
          (filters.assignedTo
            ? caseItem.assignedTo === filters.assignedTo
            : true) &&
          (filters.type ? caseItem.type === filters.type : true)
      )
      .sort((a, b) => {
        const priorityOrder = { High: 3, Medium: 2, Low: 1 };
        return (
          priorityOrder[b.priority] - priorityOrder[a.priority] ||
          new Date(b.lastUpdated) - new Date(a.lastUpdated)
        );
      });
  }, [cases, filters]);

  const getStatusColor = (status) => {
    switch (status) {
      case "Completed":
        return "success";
      case "In Progress":
        return "primary";
      case "Pending Review":
      case "Waiting for Client":
        return "warning";
      case "On Hold":
        return "error";
      default:
        return "default";
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "High":
        return "error";
      case "Medium":
        return "warning";
      case "Low":
        return "info";
      default:
        return "default";
    }
  };

  const statusCounts = useMemo(
    () => ({
      inProgress: cases.filter((c) => c.status === "In Progress").length,
      pending: cases.filter((c) => c.status === "Pending Review").length,
      waiting: cases.filter((c) => c.status === "Waiting for Client").length,
      completed: cases.filter((c) => c.status === "Completed").length,
      onHold: cases.filter((c) => c.status === "On Hold").length,
    }),
    [cases]
  );

  return (
    <Layout>
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={3} alignItems="center" sx={{ mb: 3 }}>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, status: e.target.value }))
                }
                label="Status"
              >
                <MenuItem value="">All Statuses</MenuItem>
                {CASE_STATUSES.map((status) => (
                  <MenuItem key={status} value={status}>
                    {status}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                value={filters.type}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, type: e.target.value }))
                }
                label="Type"
              >
                <MenuItem value="">All Types</MenuItem>
                {CASE_TYPES.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Priority</InputLabel>
              <Select
                value={filters.priority}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, priority: e.target.value }))
                }
                label="Priority"
              >
                <MenuItem value="">All Priorities</MenuItem>
                {PRIORITIES.map((priority) => (
                  <MenuItem key={priority} value={priority}>
                    {priority}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Assigned To</InputLabel>
              <Select
                value={filters.assignedTo}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    assignedTo: e.target.value,
                  }))
                }
                label="Assigned To"
              >
                <MenuItem value="">All Users</MenuItem>
                {[...new Set(cases.map((c) => c.assignedTo))].map((user) => (
                  <MenuItem key={user} value={user}>
                    {user}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={3}>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
            >
              <Link
                style={{ textDecoration: "none", color: "inherit" }}
                to="/NewCase"
              >
                New Case
              </Link>
            </Button>
          </Grid>
        </Grid>

        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={2.4}>
            <Card>
              <CardContent>
                <AssignmentIndIcon
                  color="primary"
                  sx={{ fontSize: 40, mb: 1 }}
                />
                <Typography variant="subtitle1">In Progress</Typography>
                <Typography variant="h4">{statusCounts.inProgress}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={2.4}>
            <Card>
              <CardContent>
                <AssignmentLateIcon
                  color="warning"
                  sx={{ fontSize: 40, mb: 1 }}
                />
                <Typography variant="subtitle1">Pending Review</Typography>
                <Typography variant="h4">{statusCounts.pending}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={2.4}>
            <Card>
              <CardContent>
                <AssignmentLateIcon color="info" sx={{ fontSize: 40, mb: 1 }} />
                <Typography variant="subtitle1">Waiting for Client</Typography>
                <Typography variant="h4">{statusCounts.waiting}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={2.4}>
            <Card>
              <CardContent>
                <AssignmentTurnedInIcon
                  color="success"
                  sx={{ fontSize: 40, mb: 1 }}
                />
                <Typography variant="subtitle1">Completed</Typography>
                <Typography variant="h4">{statusCounts.completed}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={2.4}>
            <Card>
              <CardContent>
                <AssignmentLateIcon
                  color="error"
                  sx={{ fontSize: 40, mb: 1 }}
                />
                <Typography variant="subtitle1">On Hold</Typography>
                <Typography variant="h4">{statusCounts.onHold}</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <TableContainer component={Card}>
          {loading ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                padding: "2rem",
              }}
            >
              <CircularProgress />
            </div>
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Case ID</TableCell>
                  <TableCell>Client</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Priority</TableCell>
                  <TableCell>Assigned To</TableCell>
                  <TableCell>Open Date</TableCell>
                  <TableCell>Last Updated</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredCases.map((caseItem) => (
                  <TableRow key={caseItem._id} hover>
                    <TableCell>{caseItem.id}</TableCell>
                    <TableCell>{caseItem.client}</TableCell>
                    <TableCell>{caseItem.type}</TableCell>
                    <TableCell>
                      <Chip
                        label={caseItem.status}
                        color={getStatusColor(caseItem.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={caseItem.priority}
                        color={getPriorityColor(caseItem.priority)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{caseItem.assignedTo}</TableCell>
                    <TableCell>
                      {new Date(caseItem.openDate).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      {new Date(caseItem.lastUpdated).toLocaleDateString()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </TableContainer>
      </Container>
    </Layout>
  );
};

export default CaseTracking;
